/**
 * Platform Staff — Optum-internal users with a PlatformRole.
 *
 * Promotion / demotion / revoke is admin-only. Listing is visible to
 * both platform admins and viewers (require_internal on the backend).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cloneElement, useMemo, useState, type ReactElement, type ReactNode } from 'react';

import { ApiError } from '@optum/api-client/client';
import {
  listPlatformStaff,
  revokePlatformStaff,
  setPlatformStaffRole,
} from '@optum/api-client/endpoints/platform-staff';
import {
  listPlatformStaffInvitations,
  revokePlatformStaffInvitation,
} from '@optum/api-client/endpoints/platform-staff-invitations';
import type { PlatformStaff, PlatformStaffRole } from '@optum/api-client/types';

import { useAuth } from '../../auth/AuthContext.js';
import { useRecordPanel } from '../../components/shell/record.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { Tabs, type TabItem } from '../../ui/Tabs.js';
import { useToast } from '../../ui/useToast.js';
import { absoluteDate, absoluteDateTime } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { useConfirm } from '../../ui/Confirm.js';
import { DataTable } from '../../ui/DataTable.js';
import { Dropdown, DropdownItem } from '../../ui/Dropdown.js';
import { ListShape } from '../../ui/shapes.js';
import { TTL_DEFAULT } from './invite-ttl.js';
import { Avatar, Badge } from '../../ui/finixt.js';

const ROLE_OPTIONS: { value: PlatformStaffRole; label: string }[] = [
  { value: 'optum-platform-admin', label: 'optum-platform-admin' },
  { value: 'optum-platform-viewer', label: 'optum-platform-viewer' },
];

function fmtTs(value: string | null): string {
  if (!value) return '—';
  return absoluteDateTime(value);
}

function initial(email: string): string {
  return (email.trim()[0] ?? '?').toUpperCase();
}

type StaffTab = 'operators' | 'invitations';

export function PlatformStaffPage() {
  const panel = useRecordPanel();
  const [tab, setTab] = useState<StaffTab>('operators');
  const { isPlatformAdmin, user } = useAuth();
  const currentStaffId = user?.staff_id ?? null;
  const query = useQuery({ queryKey: qk.platformStaff(), queryFn: listPlatformStaff });

  const invitations = useQuery({
    queryKey: qk.platformStaffInvitations(),
    queryFn: listPlatformStaffInvitations,
  });
  const pendingInvites = (invitations.data ?? []).filter(
    (i) => i.used_at === null && i.revoked_at === null,
  );

  const orderedStaff = useMemo(() => {
    const rows = query.data ?? [];
    if (!currentStaffId) return rows;
    const isSelf = (s: PlatformStaff) => s.id === currentStaffId;
    const self = rows.find(isSelf);
    if (!self) return rows;
    return [self, ...rows.filter((s) => !isSelf(s))];
  }, [query.data, currentStaffId]);

  const staff = query.data ?? [];
  const admins = staff.filter((r) => r.role === 'optum-platform-admin').length;

  const tabs: TabItem<StaffTab>[] = [
    { value: 'operators', label: 'Operators', icon: 'badge', count: staff.length },
    ...(isPlatformAdmin
      ? ([
          {
            value: 'invitations',
            label: 'Invitations',
            icon: 'mail',
            count: pendingInvites.length,
          },
        ] as TabItem<StaffTab>[])
      : []),
  ];

  return (
    <ListShape
      title="Staff"
      subtitle="Who operates this platform. Nothing here belongs to a tenant."
      action={
        isPlatformAdmin ? (
          <Button
            variant="primary"
            icon="person_add"
            onClick={() => panel.openForm({ name: 'invite-staff' })}
          >
            Invite staff
          </Button>
        ) : undefined
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22, paddingBottom: 64 }}>
        <Tabs items={tabs} value={tab} onChange={setTab} style={{ marginBottom: 16 }} />

        {tab === 'operators' && (
          <>
            {query.isPending && <LoadingBlock />}
            {query.isError && (
              <ErrorBlock
                message={query.error instanceof Error ? query.error.message : 'Failed to load'}
              />
            )}
            {query.data?.length === 0 && (
              <EmptyBlock>
                No platform staff yet. Issue an invitation above, or run
                <code> optum-agent-ops admin invite &lt;email&gt;</code> from the host.
              </EmptyBlock>
            )}
            {query.data && query.data.length > 0 && (
              <DataTable
                count={`${staff.length} operator${staff.length === 1 ? '' : 's'} · ${admins} admin${admins === 1 ? '' : 's'}`}
                hint="platform-scoped, like Overview"
                rows={orderedStaff}
                selectedKey={panel.current?.kind === 'staff' ? panel.current.id : undefined}
                onRowClick={(row) => panel.open({ kind: 'staff', id: row.id })}
                columns={[
                  {
                    key: 'email',
                    label: 'Operator',
                    strong: true,
                    wrap: true,
                    render: (row) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
                        <Avatar initials={initial(row.email)} size={32} />
                        <span style={{ minWidth: 0 }}>
                          <span
                            style={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: 'var(--text-body-md)',
                              fontWeight: 'var(--weight-medium)',
                              color: 'var(--text-strong)',
                            }}
                          >
                            {row.email}
                          </span>
                          {/* The operator's own states, not separate dimensions: who you are,
                              whether you have ever signed in, whether the account still works.
                              They were three columns; nobody scans a list by any of them. */}
                          <span
                            style={{
                              marginTop: 5,
                              display: 'flex',
                              flexWrap: 'wrap',
                              alignItems: 'center',
                              gap: 7,
                            }}
                          >
                            {row.id === currentStaffId && <Badge tone="blue">you</Badge>}
                            {!row.external_sub && <Badge tone="warn">pending first sign-in</Badge>}
                            {row.status && row.status !== 'active' && (
                              <StatusBadge status={row.status} />
                            )}
                          </span>
                        </span>
                      </div>
                    ),
                  },
                  {
                    key: 'role',
                    label: 'Role',
                    render: (row) => (
                      <Badge tone={row.role === 'optum-platform-admin' ? 'blue' : 'neutral'}>
                        {row.role.replace('optum-platform-', 'platform-')}
                      </Badge>
                    ),
                  },
                  {
                    key: 'created_at',
                    label: 'Added',
                    mono: true,
                    render: (row) => absoluteDate(row.created_at),
                  },
                  {
                    key: 'actions',
                    label: 'Actions',
                    align: 'right',
                    render: (row) => (
                      <StaffActions
                        staff={row}
                        canMutate={isPlatformAdmin}
                        isSelf={row.id === currentStaffId}
                      />
                    ),
                  },
                ]}
              />
            )}
          </>
        )}

        {isPlatformAdmin && tab === 'invitations' && <InvitationsSection />}
      </div>
    </ListShape>
  );
}

/**
 * Role editing, as one action rather than a column.
 *
 * It was an inline `Select`, which is the widest control in the table and made
 * Role a column about editing instead of a column you read. The design draws a
 * button; the current role stays legible as the badge beside it.
 */
function ChangeRoleMenu({
  current,
  disabled,
  onSelect,
}: {
  current: PlatformStaffRole;
  disabled: boolean;
  onSelect: (role: PlatformStaffRole) => void;
}) {
  return (
    <Dropdown
      align="end"
      width={256}
      trigger={
        <Button size="sm" variant="secondary" disabled={disabled}>
          {disabled ? 'Saving…' : 'Change role'}
        </Button>
      }
    >
      {ROLE_OPTIONS.map((opt) => (
        <DropdownItem
          key={opt.value}
          label={opt.label}
          checked={opt.value === current}
          disabled={opt.value === current}
          onSelect={() => onSelect(opt.value)}
        />
      ))}
    </Dropdown>
  );
}

function StaffActions({
  staff,
  canMutate,
  isSelf,
}: {
  staff: PlatformStaff;
  canMutate: boolean;
  isSelf: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const setRole = useMutation({
    mutationFn: (role: PlatformStaffRole) => setPlatformStaffRole(staff.id, role),
    onSuccess: (out) => {
      toast.success(`Role set to ${out.role}`);
      qc.invalidateQueries({ queryKey: qk.platformStaff() });
    },
    onError: (e) => toast.error('Set role failed', e instanceof ApiError ? e.message : String(e)),
  });

  const revoke = useMutation({
    mutationFn: () => revokePlatformStaff(staff.id),
    onSuccess: () => {
      toast.success(`Revoked staff role from ${staff.email}`);
      qc.invalidateQueries({ queryKey: qk.platformStaff() });
    },
    onError: (e) => toast.error('Revoke failed', e instanceof ApiError ? e.message : String(e)),
  });

  const canEditRow = canMutate && !isSelf;
  if (isSelf)
    return (
      <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
        (cannot revoke yourself)
      </span>
    );
  if (!canEditRow) return null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
      <ChangeRoleMenu
        current={staff.role}
        disabled={setRole.isPending}
        onSelect={(role) => setRole.mutate(role)}
      />
      <ConfirmAction
        trigger={
          <Button size="sm" variant="danger" disabled={revoke.isPending}>
            {revoke.isPending ? 'Revoking…' : 'Revoke'}
          </Button>
        }
        title="Revoke platform staff role?"
        description={
          <>
            <strong>{staff.email}</strong> will lose Optum-staff access. The last{' '}
            <code>optum-platform-admin</code> cannot be revoked.
          </>
        }
        confirmLabel="Revoke"
        onConfirm={() => revoke.mutate()}
      />
    </span>
  );
}

interface ConfirmActionProps {
  trigger: ReactElement<{ onClick?: (() => void) | undefined }>;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
}

function ConfirmAction({
  trigger,
  title,
  description,
  confirmLabel,
  onConfirm,
}: ConfirmActionProps) {
  const confirm = useConfirm();
  return cloneElement(trigger, {
    onClick: () => {
      void confirm({ title, description, confirmLabel, tone: 'danger' }).then((ok) => {
        if (ok) onConfirm();
      });
    },
  });
}

function InvitationsSection() {
  const qc = useQueryClient();
  const toast = useToast();
  const query = useQuery({
    queryKey: qk.platformStaffInvitations(),
    queryFn: listPlatformStaffInvitations,
  });

  const revoke = useMutation({
    mutationFn: (token: string) => revokePlatformStaffInvitation(token),
    onSuccess: () => {
      toast.success('Invitation revoked');
      qc.invalidateQueries({ queryKey: qk.platformStaffInvitations() });
    },
    onError: (e) => toast.error('Revoke failed', e instanceof ApiError ? e.message : String(e)),
  });

  const pending = useMemo(
    () => (query.data ?? []).filter((i) => i.used_at === null && i.revoked_at === null),
    [query.data],
  );

  return (
    <>
      {query.isPending && <LoadingBlock />}
      {query.isError && <ErrorBlock message="Could not load invitations." />}
      {query.isSuccess && pending.length === 0 && (
        <EmptyBlock>No invitations are awaiting redemption.</EmptyBlock>
      )}
      {pending.length > 0 && (
        <DataTable
          count={`${pending.length} awaiting redemption`}
          hint={`new invitations expire after ${TTL_DEFAULT} min by default`}
          rows={pending}
          columns={[
            { key: 'invited_role', label: 'Role', mono: true },
            {
              key: 'invited_email',
              label: 'Invited email',
              render: (inv) => inv.invited_email ?? '—',
            },
            {
              key: 'expires_at',
              label: 'Expires',
              mono: true,
              render: (inv) => fmtTs(inv.expires_at),
            },
            {
              key: 'token',
              label: 'Token',
              mono: true,
              render: (inv) => (
                <span
                  style={{
                    display: 'block',
                    maxWidth: 320,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {inv.token}
                </span>
              ),
            },
            {
              key: 'actions',
              label: 'Actions',
              align: 'right',
              render: (inv) => (
                <ConfirmAction
                  trigger={
                    <Button size="sm" variant="danger" disabled={revoke.isPending}>
                      Revoke
                    </Button>
                  }
                  title="Revoke this invitation?"
                  description={
                    <>
                      The token will stop working immediately. If the invitee already opened the
                      link, they cannot redeem it. If they already redeemed it, they keep their
                      staff role.
                    </>
                  }
                  confirmLabel="Revoke"
                  onConfirm={() => revoke.mutate(inv.token)}
                />
              ),
            },
          ]}
        />
      )}
    </>
  );
}
